import { IAdapter, IAdapterCallback, Adapter } from '@tencent/workbench';
import { reaction } from 'mobx';
import _debounce from 'lodash/debounce';
import { SCROLL_TO_CENTER } from '@editor/src/page/index/components/canvas/canvas-custom-event-name';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import {
    ModelMenuRefreshEventsKeys,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';

export const SLIDE_ADAPTER = 'slideAdapter';

// eslint-disable-next-line new-cap
@Adapter(SLIDE_ADAPTER)
export class SlideAdapter implements IAdapter<any> {
    public cachedDisableItemIds;
    public cachedCurrentSlide;

    public refreshMenu = _debounce((callbacks) => {
        if (this.getTextEditorStateService().isTextPreview) {
            return;
        }
        // 避免改动太大，先对切页场景进行刷新过滤，定位到其他场景需要可以再加
        if (this.cachedCurrentSlide !== this.getSelectionService().currentSlide) {
            this.cachedCurrentSlide = this.getSelectionService().currentSlide;
            const disableItemIds = this.getMenuManager()?.disableItemIds();
            const needRender = this.checkedNeedRender(disableItemIds);
            if (needRender) {
                this.cachedDisableItemIds = disableItemIds;
                callbacks.onSelectionChange();
            }
        } else {
            callbacks.onSelectionChange();
        }
    });

    public attachEditor(element: HTMLElement, callbacks: IAdapterCallback):  | undefined {
        this.bindCallBacks(callbacks);
        const editorId = 'ppt_editor';
        const toolbarContainerId = 'workbench-toolbar';
        const editorDom = document.getElementById(editorId);
        const pageDom = document.getElementById('pages') as HTMLDivElement;
        const toolbarContainerDom = document.getElementById(toolbarContainerId);
        if (!editorDom) {
            return undefined;
        }
        element.style.height = '100%';
        if (toolbarContainerDom) {
            toolbarContainerDom.style.boxSizing = 'content-box';
            // toolbarContainerDom.style.zIndex = '1';
        }
        element.appendChild(editorDom);
        this.autoCloseMenu();
        requestAnimationFrame(() => {
            window.dispatchEvent(new Event(SCROLL_TO_CENTER));
        });

        pageDom.style.display = 'none';
        eventBus.emit(EventKeys.WORKBENCH_ATTACH_EDITOR_DONE);
        return undefined;
    }

    public setValue() {
        // do nothing
    }

    public requestFocus() {
        // do nothing
    }

    public requestBlur() {
        // do nothing
    }

    public isCaret() {
        // do nothing
        return false;
    }

    public destroy() {
        // do nothing
    }

    private getMenuManager = () => this.getAppServices().editableServiceLayer?.menuManager;

    private getAppServices = (): IEditorAppServices => AppServiceProvider.getInstance().getAppServices() as
        IEditorAppServices;

    private getSelectionService = () => this.getAppServices().viewableServiceLayer?.selectionService;

    private getTextEditorStateService = () => this.getAppServices().viewableServiceLayer?.textEditorStateService;

    private getUserPriviledgeService = () => this.getAppServices().viewableServiceLayer.userPriviledgeService;

    private getPreviewService = () => this.getAppServices().editableServiceLayer?.previewService;

    private getFormatBrushManager = () => this.getAppServices().editableServiceLayer?.formatBrushManager;

    private getMasterLayoutEditService = () => {
        if (browser.isMobile || !this.getUserPriviledgeService().isEditable()) {
            return undefined;
        }
        return (this.getAppServices() as IPcEditorAppServices).editableServiceLayer?.masterLayoutEditService;
    };

    // 绑定一些事件
    private bindCallBacks(callbacks: IAdapterCallback) {
        reaction(() => [
            this.getSelectionService().getSelectedModel(),
            this.getMasterLayoutEditService()?.isMasterEditType,
        ], () => {
            callbacks.onSelectionChange();
        });
        reaction(() => this.getFormatBrushManager()?.formatBrushState.data, callbacks.onDataChange);
        // 一个图形里有多个字体时，点击不同字体，selectionStyles会发生变化
        reaction(() => this.getMenuManager()?.toolbarMenu(), () => {
            if (this.getTextEditorStateService().isTextPreview || this.getPreviewService()?.isPreview) {
                return;
            }
            this.refreshMenu(callbacks);
        });
        // table各单元格是不同字体的时候，切换单元格不会触发上述的reaction
        this.getMenuManager()?.event.on(ModelMenuRefreshEventsKeys.REFRESH_MENU_VALUES_VALUES, () => {
            this.refreshMenu(callbacks);
        });
        // changeset变更后刷新workbench
        SlideDataServices.SDKDataServices.EventManager.promise().then(({ mutationEvents }) => {
            mutationEvents.onMutationApplied(() => {
                callbacks.onSelectionChange();
            });
        });
        this.bindChartCallbacks(callbacks);
    }

    private bindChartCallbacks(callbacks: IAdapterCallback) {
        reaction(
            () => [
                (this.getSelectionService().getSelectedModel() as IChartModel).selectionStyles,
                (this.getSelectionService().getSelectedModel() as IChartModel).selectedElement,
            ],
            (selectionStyles) => {
                if (selectionStyles && this.getSelectionService().getSelectedModel()?.type === UIModelType.CHART) {
                    // 图表标题选区更变触发wb更新
                    callbacks.onSelectionChange();
                }
            },
        );
    }

    // 迁移老top-menu的菜单关闭监听，用于兼容，右键也接入到wb后废弃，需要重写
    private autoCloseMenu() {
        reaction(
            () => this.getMenuManager()?.hideAllMenuNum,
            (number) => {
                if (number) {
                    (this.getAppServices() as IPcEditorAppServices).viewableServiceLayer.
                        rightMenuManager.dangerousInstance?.hideAllMenu();
                }
            },
        );
    }

    /**
     * 根据缓存对比toolbar禁用状态，判断是否需要刷新wb
     * 只对toolbar进行比较，selection未来可能会影响侧边栏刷新，因此不能根据toolbar状态来决定是否刷新
     * @param disableItemIds
     * @private
     */
    private checkedNeedRender(disableItemIds) {
        let flag = false;
        // 如果缓存过禁用状态
        if (this.cachedDisableItemIds?.length) {
            // 先对比长度
            if (this.cachedDisableItemIds.length !== disableItemIds.length) {
                return true;
            }

            // 再对比id
            for (let index = 0; index < this.cachedDisableItemIds.length; index++) {
                const cachedItem = this.cachedDisableItemIds[index];
                const item = disableItemIds[index];
                if (item.id !== cachedItem.id) {
                    flag = true;
                    break;
                }
            }
        } else {
            flag = true;
        }
        return flag;
    }
}
